import React from 'react';
import { Card, Space, Typography } from 'antd';
import {
    CarOutlined,
    ShopOutlined,
    PhoneOutlined,
    LockOutlined,
} from '@ant-design/icons';
import './DeliveryTypeStep.css';

export type DeliveryType = 'shipping' | 'takeaway' | 'arrange';

interface DeliveryOption {
    type: DeliveryType;
    icon: React.ReactNode;
    title: string;
    description: string;
    available: boolean;
}

interface DeliveryTypeStepProps {
    value?: DeliveryType;
    onChange: (type: DeliveryType) => void;
    hasShipping: boolean;
    hasTakeaway: boolean;
    hasArrange?: boolean;
    disabled?: boolean;
}

export const DeliveryTypeStep: React.FC<DeliveryTypeStepProps> = ({
    value,
    onChange,
    hasShipping,
    hasTakeaway,
    hasArrange = false,
    disabled = false,
}) => {
    const options: DeliveryOption[] = ([
        {
            type: 'shipping' as const,
            icon: <CarOutlined style={{ fontSize: '1.5rem' }} />,
            title: 'Envío a Domicilio',
            description: 'Recibí tu pedido en la dirección que indiques',
            available: hasShipping,
        },
        {
            type: 'takeaway' as const,
            icon: <ShopOutlined style={{ fontSize: '1.5rem' }} />,
            title: 'Retiro en Tienda',
            description: 'Retirá tu pedido en la sucursal seleccionada',
            available: hasTakeaway,
        },
        {
            type: 'arrange' as const,
            icon: <PhoneOutlined style={{ fontSize: '1.5rem' }} />,
            title: 'Coordinar con Vendedor',
            description: 'Te contactaremos para acordar la entrega',
            available: hasArrange,
        },
    ] as DeliveryOption[]).filter((opt) => opt.available);

    // If no options available, show a message
    if (options.length === 0) {
        return (
            <Card className="delivery-type-step bg-secondary border-default" size="small" variant="borderless">
                <Typography.Text type="secondary">
                    No hay métodos de entrega disponibles para este pedido.
                </Typography.Text>
            </Card>
        );
    }

    return (
        <Card
            title="Método de Entrega" // Eliminado "Paso 2:"
            className="delivery-type-step bg-transparent"
            size="small"
            variant="borderless"
            styles={{
                header: { borderBottom: 'none', paddingLeft: 0, fontSize: '1.1rem' },
                body: { padding: 0 }
            }}
        >
            <div className="delivery-toggles-container">
                {options.map((option) => {
                    const isSelected = value === option.type;
                    return (
                        <div
                            key={option.type}
                            className={`delivery-toggle-card ${isSelected ? 'selected' : ''} ${disabled ? 'disabled' : ''}`}
                            onClick={() => !disabled && onChange(option.type)}
                            role="radio"
                            aria-checked={isSelected}
                            tabIndex={disabled ? -1 : 0}
                        >
                            <div className="delivery-toggle-icon">
                                {option.icon}
                            </div>
                            <div className="delivery-toggle-content">
                                <Typography.Text strong className="d-block">
                                    {option.title}
                                </Typography.Text>
                                <Typography.Text type="secondary" className="d-none d-md-block small mt-1">
                                    {option.description}
                                </Typography.Text>
                            </div>
                            {isSelected && (
                                <div className="delivery-toggle-check">
                                    <div className="check-circle" />
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </Card>
    );
};


interface PendingStepCardProps {
    title: string;
    stepNumber: number;
}

export const PendingStepCard: React.FC<PendingStepCardProps> = ({ title, stepNumber }) => (
    <Card className="pending-step-card" size="small">
        <Space align="center">
            <span className="pending-step-card__icon">
                <LockOutlined />
            </span>
            <div>
                <Typography.Text type="secondary">
                    Paso {stepNumber}: {title}
                </Typography.Text>
                <Typography.Text type="secondary" className="d-block small">
                    Completa el paso anterior para desbloquear
                </Typography.Text>
            </div>
        </Space>
    </Card>
);

export default DeliveryTypeStep;
